import React, { useState } from 'react';
import {
    Button,
    Col,
    Divider,
    Input,
    Modal as ServaModal,
    Row,
    Text,
} from '@servahealth/serva-ui-kit';

export interface AIContextAssistModalProps {
    open: boolean;
    title: string;
    currentSectionTitle: string;
    requestSectionTitle: string;
    resultSectionTitle: string;
    ancillarySectionTitle: string;
    currentText: string;
    resultText: string;
    ancillaryItems: string[];
    requestPlaceholder: string;
    askButtonText: string;
    useButtonText: string;
    loading?: boolean;
    allowUseSuggestion?: boolean;
    onAskAI: (requestText: string) => void;
    onCopy: (resultText: string) => void;
    onUseSuggestion: (resultText: string) => void;
    onClose: () => void;
}

export const AIContextAssistModal: React.FC<AIContextAssistModalProps> = ({ ...props }) => {

    const [requestText, setRequestText] = useState('');

    const boxStyle: React.CSSProperties = {
        backgroundColor: 'white',
        border: '1px solid #d9d9d9',
        borderRadius: '4px',
        color: 'black',
        fontFamily: 'Consolas, monospace',
        fontSize: '13px',
        height: '96px',
        lineHeight: '18px',
        overflowY: 'auto',
        padding: '8px',
        textAlign: 'left',
        whiteSpace: 'pre-wrap',
        width: '100%',
        wordBreak: 'break-word',
    };

    const labelStyle: React.CSSProperties = {
        display: 'block',
        marginBottom: '4px',
        textAlign: 'left',
    };

    const canUseSuggestion =
        (props.allowUseSuggestion ?? true) &&
        props.resultText.trim().length > 0;

    const handleAskAI = () => {
        props.onAskAI(requestText);
    };

    const handleClose = () => {
        setRequestText('');
        props.onClose();
    };

    return (
        <ServaModal
            open={props.open}
            title={
                <div
                    style={{
                        color: 'black',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        width: '100%',
                    }}
                >
                    {props.title}
                </div>
            }
            width={850}
            okButtonProps={{ style: { display: 'none' } }}
            cancelButtonProps={{ style: { display: 'none' } }}
            onCancel={handleClose}
        >
            <Divider style={{ borderTop: '1px solid black', margin: '1px 0 8px 0' }} />

            <Row gutter={16} style={{ paddingBottom: '8px' }}>
                <Col span={24}>
                    <Text italic strong style={labelStyle}>{props.currentSectionTitle}</Text>
                    <div style={boxStyle}>
                        {props.currentText.trim().length > 0 ? props.currentText : '(Not Set)'}
                    </div>
                </Col>
            </Row>

            <Row gutter={16} style={{ paddingBottom: '8px' }}>
                <Col span={24}>
                    <Text italic strong style={labelStyle}>{props.requestSectionTitle}</Text>
                    <Input.TextArea
                        value={requestText}
                        onChange={(event) => setRequestText(event.target.value)}
                        placeholder={props.requestPlaceholder}
                        style={{
                            ...boxStyle,
                            fontFamily: 'inherit',
                            resize: 'none',
                        }}
                    />
                </Col>
            </Row>

            <Row gutter={16} style={{ paddingBottom: '8px' }}>
                <Col span={24}>
                    <div style={{ textAlign: 'left' }}>
                        <Button
                            disabled={requestText.trim().length === 0 || props.loading}
                            loading={props.loading}
                            type='primary'
                            onClick={handleAskAI}
                        >
                            {props.askButtonText}
                        </Button>
                    </div>
                </Col>
            </Row>

            <Row gutter={16} style={{ paddingBottom: '8px' }}>
                <Col span={24}>
                    <Text italic strong style={labelStyle}>{props.resultSectionTitle}</Text>
                    <div style={boxStyle}>
                        {props.resultText}
                    </div>
                </Col>
            </Row>

            <Row gutter={16} style={{ paddingBottom: '8px' }}>
                <Col span={24}>
                    <Text italic strong style={labelStyle}>{props.ancillarySectionTitle}</Text>
                    <div style={boxStyle}>
                        {
                            props.ancillaryItems.length > 0
                                ? props.ancillaryItems.map((item) => `- ${item}`).join('\n')
                                : ''
                        }
                    </div>
                </Col>
            </Row>

            <Divider style={{ borderTop: '1px solid black', margin: '1px 0 8px 0' }} />

            <Row gutter={16}>
                <Col span={12}>
                    <div style={{ textAlign: 'left' }}>
                        <Button
                            disabled={props.resultText.trim().length === 0}
                            onClick={() => props.onCopy(props.resultText)}
                        >
                            Copy
                        </Button>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ textAlign: 'right' }}>
                        {
                            (props.allowUseSuggestion ?? true) &&
                            (
                                <Button
                                    disabled={!canUseSuggestion}
                                    style={{ marginRight: '8px' }}
                                    type='primary'
                                    onClick={() => props.onUseSuggestion(props.resultText)}
                                >
                                    {props.useButtonText}
                                </Button>
                            )
                        }
                        <Button onClick={handleClose}>
                            Close
                        </Button>
                    </div>
                </Col>
            </Row>
        </ServaModal>
    );
};

export default AIContextAssistModal;
